import { Cartesian2, Cartesian3 } from "cesium";
import { AssetType, } from "../types";
import { EarthBrainUtils } from "./earthbrainutility";
import { ZBoundedPlane } from "../core/z-bounded-plane";
import { intersectGeometryByPlane } from "./geometry-utility";
export var CrossSectionUtils;
(function (CrossSectionUtils) {
    /**
     * Calculates cross-section data at a specific point by tracing left and right outlines.
     *
     * @param point - The cross-section point containing position, direction, and distance information
     * @param traceableMaps - Map of traceable assets (point clouds, trenches, roads, etc.) grouped by asset type
     * @returns Cross-section data containing the following properties:
     * - `referencePoint`: The original input point used as the center reference for the cross-section
     * - `referencePointHeight`: Array containing [distance, height] pair where distance is 0 and height is the z-coordinate of the reference point
     * - `outlineHeightAtReference`: The height of the traced outline at the reference point position, obtained from the first available point in right or left outline
     * - `leftOutline`: Traced outline data for the left side (negative x-direction), with negated 2D coordinates and reversed 3D array for proper rendering
     * - `rightOutline`: Traced outline data for the right side (positive x-direction)
     * - `leftCrossSectionPolyline`: Empty array reserved for left cross-section polyline data
     * - `rightCrossSectionPolyline`: Empty array reserved for right cross-section polyline data
     */
    function calculateCrossSectionAtEachPoint(point, traceableMaps) {
        //left/right point are the end points to generate cross section
        const leftPoint = EarthBrainUtils.getCartesianPointAtDistanceFromCartesian(point.position, //reference/start Point used to generate cross Section
        point.leftOutlineData.direction, point.leftOutlineData.distance);
        const rightPoint = EarthBrainUtils.getCartesianPointAtDistanceFromCartesian(point.position, point.rightOutlineData.direction, point.rightOutlineData.distance);
        let leftOutline = {
            outline2D: new Map(),
            outline3D: [],
        };
        /**
         * If width is 0 then the reference point position and the left/right point would become equal
         * thereby causing errors while calculating interpolated points between start(reference point
         * position) and end(left/right points) points.
         * Thus a check is added which prevents calculating trace outline if both are equal and
         * returns empty left and right outlines.
         */
        if (!leftPoint.equals(point.position)) {
            leftOutline = CrossSectionUtils.traceOutlineBetweenPoints(point.position, leftPoint, point.sampleSize, traceableMaps);
            //need to be negative as it needs to be rendered on other side
            leftOutline.outline2D.forEach((outlinesArr) => {
                outlinesArr.forEach((outlineArr) => {
                    outlineArr.forEach((outline) => {
                        outline.forEach((point) => {
                            point[0] = -point[0];
                        });
                    });
                });
            });
            //needs to be flipped to make it continous in viewer.
            leftOutline.outline3D.reverse();
        }
        let rightOutline = {
            outline2D: new Map(),
            outline3D: [],
        };
        if (!rightPoint.equals(point.position)) {
            rightOutline = CrossSectionUtils.traceOutlineBetweenPoints(point.position, rightPoint, point.sampleSize, traceableMaps);
        }
        const refPointHeight = [];
        // x:0 , y: point.z as x is distance from reference point and y is height value.
        refPointHeight.push([0, point.position.z]);
        // Get outline height at reference point position.
        // The outlines are traced from the reference point, so the endpoint of each outline
        // contains the reference point height. Prefer right outline if available, otherwise use left outline.
        let outlineHeightAtReference = point.position.z;
        if (rightOutline.outline3D.length > 0 &&
            rightOutline.outline3D[0].length > 0) {
            outlineHeightAtReference = rightOutline.outline3D[0][0].z;
        }
        else if (leftOutline.outline3D.length > 0 &&
            leftOutline.outline3D[leftOutline.outline3D.length - 1].length > 0) {
            outlineHeightAtReference =
                leftOutline.outline3D[leftOutline.outline3D.length - 1][0].z;
        }
        return {
            referencePoint: point,
            referencePointHeight: refPointHeight,
            outlineHeightAtReference,
            leftOutline,
            rightOutline,
            leftCrossSectionPolyline: [],
            rightCrossSectionPolyline: [],
        };
    }
    CrossSectionUtils.calculateCrossSectionAtEachPoint = calculateCrossSectionAtEachPoint;
    /**
     *
     * @param startPoint is the point from which tracing starts
     * @param endPoint is the point from which tracing ends
     * @param sampleSize is the step size which will be used to sample from start point to end point
     * @param traceablemapsMap is the map of height maps grouped as per asset type, on which tracing will happen
     * @returns
     */
    function traceOutlineBetweenPoints3D(interpolatedPoints, traceablemapsMap) {
        /**
         *
         * @param heights in two dimentional array m * n
         * @param callback should process the m number of heights and provide one value
         * @returns n muner of heights
         * For m * n array, it merges them per column to 1 * n array
         */
        const getMergedHeights = (heights, callback) => Array.from({ length: heights[0]?.length || 0 }, (_, index) => index).map((i) => callback(heights.map((hs) => hs[i])));
        /**
         *
         * @param tracePoints are points which needs to be traced in heightmaps
         * @param heightMapMap are array of height maps where trace points are traced
         * @param callback For given an array of values, it should merge/ reduce them into single values. In here list of height with same x,y will be reduced into single value
         * @returns array of values
         */
        const getMergedTracedHeights = (tracePoints, traceableMaps, callback) => {
            const tracedPoints = traceableMaps.map((th) => th.tracePoints(tracePoints));
            const heights = tracedPoints.map((tps) => tps.map((tp) => (tp ? tp.z : Number.NaN)));
            return getMergedHeights(heights, callback);
        };
        /**
         *
         * @param tracePoints are points which needs to be traced in heightmaps
         * @param traceableMapMap is the Map of heightmaps grouped as per assettype
         * @param callback For given an array of values, it should merge/ reduce them into single values. In here list of height with same x,y will be reduced into single value
         * @returns array of values
         */
        const getMergedTracedHeightsFromHeightMapMaps = (tracePoints, traceableMapMap, callback) => {
            const traceableMaps = Array.from(traceableMapMap.values()).flat();
            return getMergedTracedHeights(tracePoints, traceableMaps, callback);
        };
        let finalHeights;
        const trenchHeights = traceablemapsMap.get(AssetType.Trench_Data);
        const maxHeight = (arr) => {
            const val = Math.max(...arr.filter((n) => !isNaN(n) && n !== undefined));
            return val && isFinite(val) ? val : undefined;
        };
        if (trenchHeights && trenchHeights.length > 0) {
            const heights = getMergedTracedHeights(interpolatedPoints, trenchHeights, maxHeight);
            traceablemapsMap.delete(AssetType.Trench_Data);
            const oheights = getMergedTracedHeightsFromHeightMapMaps(interpolatedPoints, traceablemapsMap, maxHeight);
            const getFirstIfPossible = ([a, b]) => !!a && !isNaN(a) ? a : b;
            finalHeights = getMergedHeights([heights, oheights], getFirstIfPossible);
            traceablemapsMap.set(AssetType.Trench_Data, trenchHeights);
        }
        else {
            finalHeights = getMergedTracedHeightsFromHeightMapMaps(interpolatedPoints, traceablemapsMap, maxHeight);
        }
        const finalOutlineLocal3D = interpolatedPoints.map((p, i) => {
            if (finalHeights[i] !== undefined) {
                const point = p.clone();
                point.z = finalHeights[i];
                return point;
            }
            else {
                return undefined;
            }
        });
        const outline3D = getChunkedArray(finalOutlineLocal3D);
        return outline3D;
    }
    CrossSectionUtils.traceOutlineBetweenPoints3D = traceOutlineBetweenPoints3D;
    function traceOutlineBetweenPoints2D(startPoint, traceablemapsMap, plane, interpolatedPoints) {
        const outline2d = new Map();
        traceablemapsMap.forEach((tArray, assetType) => {
            const outlines = [];
            tArray.forEach((traceable) => {
                const tracedPoints = traceable.tracePoints(interpolatedPoints);
                const outline = create2DOutlines(startPoint, tracedPoints);
                outlines.push(outline);
            });
            outline2d.set(assetType, outlines);
        });
        return outline2d;
    }
    CrossSectionUtils.traceOutlineBetweenPoints2D = traceOutlineBetweenPoints2D;
    function traceOutlineBetweenPoints(startPoint, endPoint, sampleSize, traceablemapsMap) {
        const interpolatedPoints = EarthBrainUtils.getLinearInterpolation(startPoint, endPoint, sampleSize, true, true);
        const dir = EarthBrainUtils.getDirBetweenTwoCartesians(new Cartesian3(startPoint.x, startPoint.y, 0), new Cartesian3(endPoint.x, endPoint.y, 0));
        const up = Cartesian3.UNIT_Z;
        const normal = Cartesian3.cross(dir, up, new Cartesian3());
        const center = new Cartesian3();
        Cartesian3.multiplyByScalar(Cartesian3.add(startPoint, endPoint, center), 0.5, center);
        const zBoundedPlane = ZBoundedPlane.fromPointNormal(center, normal);
        const intersectedPoints = [];
        traceablemapsMap.forEach((tArray, assetType) => {
            if (assetType !== AssetType.Point_Cloud) {
                tArray.forEach((traceable) => {
                    if (traceable.geometry) {
                        const geometryPoints = intersectGeometryByPlane(traceable.geometry, zBoundedPlane);
                        intersectedPoints.push(...geometryPoints);
                    }
                });
            }
        });
        const sameSidePoints = intersectedPoints.filter((p) => {
            const pV = Cartesian2.subtract(p, startPoint, new Cartesian2());
            const dot = Cartesian2.dot(dir, pV);
            return dot >= 0;
        });
        const distance2d = Cartesian2.distance(startPoint, endPoint);
        const boundedsameSidePoints = sameSidePoints.filter((p) => Cartesian2.distance(startPoint, p) <= distance2d);
        const sortBy2DDistanceFromStartPoint = (points, start) => {
            points.sort((a, b) => Cartesian2.distance(start, a) - Cartesian2.distance(start, b)); // Assending Order
        };
        if (boundedsameSidePoints.length > 0) {
            sortBy2DDistanceFromStartPoint(boundedsameSidePoints, startPoint);
            if (Cartesian2.distance(startPoint, boundedsameSidePoints[0]) < 0.001) {
                interpolatedPoints.shift();
            }
        }
        const finalInterpolatedPoints = [
            ...boundedsameSidePoints,
            ...interpolatedPoints,
        ];
        sortBy2DDistanceFromStartPoint(finalInterpolatedPoints, startPoint);
        const filteredInterpolatedPoints = [];
        for (const point of finalInterpolatedPoints) {
            if (!filteredInterpolatedPoints[filteredInterpolatedPoints.length - 1] ||
                Cartesian3.distance(filteredInterpolatedPoints[filteredInterpolatedPoints.length - 1], point) > 0.00000001) {
                filteredInterpolatedPoints.push(point);
            }
        }
        const outline3D = traceOutlineBetweenPoints3D(filteredInterpolatedPoints, traceablemapsMap);
        const outline2D = traceOutlineBetweenPoints2D(startPoint, traceablemapsMap, zBoundedPlane, filteredInterpolatedPoints);
        return {
            outline3D,
            outline2D,
        };
    }
    CrossSectionUtils.traceOutlineBetweenPoints = traceOutlineBetweenPoints;
    function create2DOutlines(refPoint, pointsLocal) {
        const outline = [];
        // Chunking to handle outline for holes in a point cloud
        let chunk = [];
        for (const element of pointsLocal) {
            // Add non-undefined values to the current chunk
            if (element !== undefined && !isNaN(element.z)) {
                const dist = Cartesian2.distance(refPoint, element);
                const height = element.z;
                chunk.push([dist, height]);
            }
            else if (chunk.length > 0) {
                // If undefined and chunk is not empty, store the current chunk and reset it
                outline.push(chunk);
                chunk = [];
            }
        }
        // Add the last chunk if not empty
        if (chunk.length > 0) {
            outline.push(chunk);
        }
        return outline;
    }
    CrossSectionUtils.create2DOutlines = create2DOutlines;
    /**
     *
     * @param finalOutline are final heights which are traced on pointcloud/trench/road/flatgrounds.
     * @returns array of Cartesian3[]
     */
    function getChunkedArray(finalOutline) {
        const result = [];
        let chunk = [];
        // Iterate through the array
        for (const value of finalOutline) {
            if (value !== undefined) {
                // Add non-undefined values to the current chunk
                chunk.push(value);
            }
            else if (chunk.length > 0) {
                // If undefined and chunk is not empty, store the current chunk and reset it
                result.push(chunk);
                chunk = [];
            }
        }
        // Add the last chunk if not empty
        if (chunk.length > 0) {
            result.push(chunk);
        }
        return result;
    }
    CrossSectionUtils.getChunkedArray = getChunkedArray;
})(CrossSectionUtils || (CrossSectionUtils = {}));
//# sourceMappingURL=cross-section-utils.js.map